import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Alert,
    AlertIcon,
    Box,
    Spinner,
    Code,
    Divider,
    useColorModeValue,
} from '@chakra-ui/react';
import { useToast } from '../hooks/useToast';
import getCSRFtoken from '../utils/getCSRFtoken';
import fetchWithAuth from '../utils/fetchWithAuth';

function PreflightModal({ isOpen, onClose, pollId, onProceed }) {
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [settingLive, setSettingLive] = useState(false);

    const toast = useToast();
    const bgColor = useColorModeValue('white', 'gray.800');
    const codeBg = useColorModeValue('gray.50', 'gray.700');

    useEffect(() => {
        if (isOpen && pollId) {
            runPreflight();
        }
    }, [isOpen, pollId]);

    const runPreflight = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchWithAuth(`/api/polls/${pollId}/preflight/`);
            if (!response.ok) throw new Error('Failed to run preflight checks');
            const data = await response.json();
            setResult(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSetLive = async () => {
        setSettingLive(true);
        try {
            const response = await fetchWithAuth(`/api/polls/${pollId}/set-live/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFtoken(),
                },
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to set poll live');
            }

            const data = await response.json();
            toast.showSuccess('Success', 'Poll is now live!');
            if (onProceed) onProceed(data);
            onClose();
        } catch (err) {
            toast.showError('Error', err.message);
        } finally {
            setSettingLive(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent bg={bgColor}>
                <ModalHeader>Pre-Flight Checks</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {loading && (
                        <VStack py={8}>
                            <Spinner size="lg" />
                            <Text color="gray.500">Running checks...</Text>
                        </VStack>
                    )}

                    {error && (
                        <Alert status="error">
                            <AlertIcon />
                            {error}
                        </Alert>
                    )}

                    {result && !loading && (
                        <VStack spacing={4} align="stretch">
                            {/* Summary */}
                            <HStack>
                                <Box
                                    w={3} h={3} borderRadius="full"
                                    bg={result.passed ? 'green.400' : 'red.400'}
                                />
                                <Text fontWeight="bold">
                                    {result.passed
                                        ? 'All checks passed'
                                        : `${result.error_count} error${result.error_count !== 1 ? 's' : ''} found`}
                                    {result.warning_count > 0 &&
                                        ` | ${result.warning_count} warning${result.warning_count !== 1 ? 's' : ''}`}
                                </Text>
                            </HStack>

                            {/* Errors */}
                            {result.errors.length > 0 && (
                                <VStack spacing={2} align="stretch">
                                    {result.errors.map((err, i) => (
                                        <Alert key={i} status="error" borderRadius="md">
                                            <AlertIcon />
                                            <Text fontSize="sm">{err.message}</Text>
                                        </Alert>
                                    ))}
                                </VStack>
                            )}

                            {/* Warnings */}
                            {result.warnings.length > 0 && (
                                <VStack spacing={2} align="stretch">
                                    {result.warnings.map((warn, i) => (
                                        <Alert key={i} status="warning" borderRadius="md">
                                            <AlertIcon />
                                            <Text fontSize="sm">{warn.message}</Text>
                                        </Alert>
                                    ))}
                                </VStack>
                            )}

                            {/* Info */}
                            {result.info.length > 0 && (
                                <VStack spacing={2} align="stretch">
                                    {result.info.map((info, i) => (
                                        <Alert key={i} status="info" borderRadius="md" variant="subtle">
                                            <AlertIcon />
                                            <Text fontSize="sm">{info.message}</Text>
                                        </Alert>
                                    ))}
                                </VStack>
                            )}

                            {/* SMS Preview */}
                            {result.sms_preview && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>SMS Preview (English)</Text>
                                        <Box bg={codeBg} p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                                            <Code display="block" whiteSpace="pre-wrap" bg="transparent" fontSize="sm">
                                                {result.sms_preview.full_message}
                                            </Code>
                                        </Box>
                                        <HStack mt={2} spacing={4} flexWrap="wrap">
                                            <Text fontSize="xs" color="gray.500">
                                                {result.sms_preview.character_count} chars
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {result.sms_preview.segments} segment{result.sms_preview.segments !== 1 ? 's' : ''}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {result.sms_preview.encoding.toUpperCase()}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {result.sms_preview.estimated_cost_per_sms}/msg
                                            </Text>
                                        </HStack>
                                    </Box>

                                    {result.sms_preview.full_message_es && (
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>SMS Preview (Spanish)</Text>
                                            <Box bg={codeBg} p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                                                <Code display="block" whiteSpace="pre-wrap" bg="transparent" fontSize="sm">
                                                    {result.sms_preview.full_message_es}
                                                </Code>
                                            </Box>
                                            <HStack mt={2} spacing={4} flexWrap="wrap">
                                                <Text fontSize="xs" color="gray.500">
                                                    {result.sms_preview.character_count_es} chars
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {result.sms_preview.segments_es} segment{result.sms_preview.segments_es !== 1 ? 's' : ''}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {result.sms_preview.encoding_es.toUpperCase()}
                                                </Text>
                                            </HStack>
                                        </Box>
                                    )}
                                </>
                            )}
                        </VStack>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose} isDisabled={settingLive}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="teal"
                        onClick={handleSetLive}
                        isLoading={settingLive}
                        loadingText="Setting live..."
                        isDisabled={loading || !result?.passed}
                    >
                        Set Live
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default PreflightModal;
